import { HttpClient } from '@angular/common/http';
import { Component, inject, type OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import * as SuperAdminAccountDetailGet from '../../../server/routes/super-admin/accounts/get';
import type { AccountClean } from '../../../server/services/account.service';
import type { SessionClean } from '../../../server/services/session.service';
import { getUserSpaces } from '../../../server/services/space.service';
import { generatePath } from '../../../server/services/utils.service';
import type { Org, OrgSpace } from '../../../server/types/org-types';
import { TitleService } from '../title.service';
import { UtilsService } from '../utils.service';

type AccountDetail = AccountClean & {
  createdAtString: string;
  updatedAtString: string;
  bannedAtString: string;
  accountLink: string;
};

type UserOrgDetail = Org & {
  createdAtString: string;
  updatedAtString: string;
  userSpaces: OrgSpace[];
  userSpacesString: string;
  orgLink: string;
};

type SessionDetail = SessionClean & {
  createdAtString: string;
  updatedAtString: string;
  expiresAtString: string;
};

@Component({
  selector: 'app-super-admin-detail',
  templateUrl: './super-admin-account-detail.component.html',
  styleUrls: ['./super-admin-account-detail.component.scss'],
  standalone: false,
})
export class SuperAdminAccountDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private titleService = inject(TitleService);
  private utils = inject(UtilsService);
  private http = inject(HttpClient);

  accountId: string;
  account: AccountDetail | undefined;

  orgs: UserOrgDetail[] | undefined = undefined;
  orgDisplayedColumns: string[] = [
    'name',
    'userSpacesString',
    'createdAtString',
    'updatedAtString',
    '_id',
  ];

  sessions: SessionDetail[] | undefined = undefined;
  sessionDisplayedColumns: string[] = [
    '_id',
    'clientId',
    'createdAtString',
    'updatedAtString',
    'expiresAtString',
    'userAgent',
  ];

  waiting = true;
  accessDenied = false;
  errorState?: 'unknownError';

  subscriptions = new Subscription();

  ngOnInit() {
    this.titleService.set('Account detail - Super admin');

    this.accountId = this.route.snapshot.params.accountId;

    const path = generatePath<SuperAdminAccountDetailGet.ReqParams>(
      '/api/super-admin/accounts/:accountId',
      { accountId: this.accountId }
    );

    const httpSubscribe = this.http
      .get<SuperAdminAccountDetailGet.ResBody>(path)
      .subscribe({
        next: ({ account, orgs, sessions }) => {
          this.account = {
            ...account,
            createdAtString: new Date(account.createdAt).toLocaleDateString(),
            updatedAtString: new Date(account.updatedAt).toLocaleDateString(),
            bannedAtString: account.bannedAt
              ? new Date(account.bannedAt).toLocaleDateString()
              : '',
            accountLink: '/super-admin/account/' + account._id,
          };

          this.orgs = orgs.map((org) => {
            const user = org.users.find((u) => u.accountId === this.accountId);

            const userSpaces = user ? getUserSpaces(org.spaces, user._id) : [];

            const userSpacesString = userSpaces.map((s) => s.name).join(', ');

            const orgLink = '/super-admin/org/' + org._id;

            const orgDetail: UserOrgDetail = {
              ...org,
              createdAtString: new Date(org.createdAt).toLocaleDateString(),
              updatedAtString: new Date(org.updatedAt).toLocaleDateString(),
              userSpaces,
              userSpacesString,
              orgLink,
            };

            return orgDetail;
          });

          this.sessions = sessions.map((session) => {
            const sessionDetail: SessionDetail = {
              ...session,
              createdAtString: new Date(session.createdAt).toLocaleDateString(),
              updatedAtString: new Date(session.updatedAt).toLocaleDateString(),
              expiresAtString: session.expiresAt
                ? new Date(session.expiresAt).toLocaleDateString()
                : '',
            };
            return sessionDetail;
          });
        },
        error: (error) => {
          this.waiting = false;
          if (error.status === 0) {
            this.utils.launchOfflineNotification();
          } else if (error.status === 403) {
            this.accessDenied = true;
          } else {
            this.errorState = 'unknownError';
            console.error(error);
          }
        },
      });
    this.subscriptions.add(httpSubscribe);
  }
}
